import type { FC } from "react";
import { durationBetweenTimestamps } from "@/format/time";
import { type PlainMessage } from "@bufbuild/protobuf";
import { Text } from "@/components/podkit/typography/Text";
import { WorkflowExecutionActionStepStatusIcon } from "@/components/automations/AutomationExecutionActionStepStatusIcon";
import { Card } from "@/components/flexkit/Card";
import type { WorkflowExecutionAction_Status_StepStatus } from "gitpod-next-api/gitpod/v1/workflow_pb";
import IconConsoleSimple from "central-icons/IconConsoleSimple";
import IconBubbleText6 from "central-icons/IconBubbleText6";

export const WorkflowExecutionActionStepCard: FC<{
    step: PlainMessage<WorkflowExecutionAction_Status_StepStatus>;
}> = ({ step }) => {
    const duration =
        step.startedAt && step.finishedAt ? durationBetweenTimestamps(step.startedAt, step.finishedAt) : undefined;

    let stepType: string | null = null;
    let stepContent: string | null = null;
    let StepIcon: typeof IconConsoleSimple | null = null;

    if (step.step?.step?.case === "task") {
        stepType = "Shell script";
        stepContent = step.step.step.value.command || "";
        StepIcon = IconConsoleSimple;
    } else if (step.step?.step?.case === "agent") {
        stepType = "Prompt";
        stepContent = step.step.step.value.prompt || "";
        StepIcon = IconBubbleText6;
    }

    const durationText = duration || (step.startedAt ? "In progress" : "Pending");

    return (
        <Card variant={"bordered"} className="border bg-surface-secondary shadow-none">
            <div className="flex items-center gap-3">
                <WorkflowExecutionActionStepStatusIcon step={step} />

                {stepType && StepIcon && (
                    <div className="flex items-center gap-1 shrink-0">
                        <StepIcon size={16} className="text-content-muted" />
                        <Text className="text-sm text-content-muted">{stepType}</Text>
                    </div>
                )}

                {stepContent && (
                    <Text className="flex-1 min-w-0 truncate font-mono text-sm text-content-secondary">
                        {stepContent}
                    </Text>
                )}

                {step.failureMessage ? (
                    <Text className="shrink-0 text-sm text-content-destructive">{step.failureMessage}</Text>
                ) : (
                    <Text className="shrink-0 text-sm text-content-muted">{durationText}</Text>
                )}
            </div>
        </Card>
    );
};
